import React, { useState } from 'react';
import { ArrowLeft, HelpCircle, Mail, ChevronDown, ChevronUp, Search, FileText, Volume2, Brain, Upload, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { useI18n } from '../../i18n';
import { WhatsAppSupport } from './whatsapp-support';

interface HelpViewProps {
  onClose: () => void;
}

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

/** FAQ bodies live in the locale files; this only fixes order and grouping. */
const FAQ_KEYS: { id: string; categoryKey: string }[] = [
  { id: 'upload', categoryKey: 'gettingStarted' },
  { id: 'audio', categoryKey: 'features' },
  { id: 'explain', categoryKey: 'features' },
  { id: 'plans', categoryKey: 'billing' },
  { id: 'cancel', categoryKey: 'billing' },
  { id: 'offline', categoryKey: 'general' },
  { id: 'notes', categoryKey: 'features' },
  { id: 'security', categoryKey: 'privacy' },
];

export function HelpView({ onClose }: HelpViewProps) {
  const { t } = useI18n();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const faqs: FAQItem[] = FAQ_KEYS.map(({ id, categoryKey }) => ({
    id,
    question: t(`help.faqs.${id}.question`),
    answer: t(`help.faqs.${id}.answer`),
    category: categoryKey,
  }));

  // 'All' is tracked by key so the selection survives a language switch.
  const categories = ['All', ...new Set(FAQ_KEYS.map(f => f.categoryKey))];

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleFAQ = (id: string) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@sourcity.ai?subject=Support Request';
    toast.success(t('help.openingEmail'));
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-gradient-to-br from-white to-gray-50">
      {/* Header */}
      <div className="shrink-0 border-b border-white/20 bg-white/60 backdrop-blur-xl">
        <div className="hidden px-4 pt-4 sm:px-6 sm:pt-5 md:block">
          <a
            href="mailto:support@sourcity.ai"
            className="text-sm font-medium text-[#7367FF] hover:underline"
          >
            support@sourcity.ai
          </a>
        </div>
        <div className="flex items-center gap-4 px-4 py-3 sm:px-6 sm:py-4">
          <Button
            variant="ghost"
            size="sm"
            aria-label={t("common.back")}
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <ArrowLeft className="h-4 w-4 text-[#434343] rtl-flip" />
          </Button>
          <h1 className="text-xl sm:text-2xl font-display text-[#434343]">{t('help.title')}</h1>
        </div>
      </div>
                  
      <ScrollArea className="min-h-0 flex-1 p-4 sm:p-6">
        <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
          <WhatsAppSupport />
          
          {/* Mobile / phone: lead with email support */}
          <div className="rounded-2xl border border-[#7367FF]/20 bg-gradient-to-r from-[#7367FF]/10 to-[#2DD2FE]/10 p-4 shadow-sm md:hidden">
            <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-[#7367FF] to-[#2DD2FE]">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#434343]">{t('help.mobileSupportTitle')}</p>
                <p className="mt-1 text-sm leading-relaxed text-[#434343]/80">
                  {t('help.mobileSupportBody')
                    .split('{{email}}')
                    .flatMap((chunk, i) =>
                      i === 0
                        ? [chunk]
                        : [
                            <a
                              key="support-mail"
                              href="mailto:support@sourcity.ai?subject=Support%20request"
                              className="font-medium text-[#7367FF] underline decoration-[#7367FF]/30 underline-offset-2"
                              dir="ltr"
                            >
                              support@sourcity.ai
                            </a>,
                            chunk,
                          ]
                    )}
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute start-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#434343]/40" />
            <Input
              aria-label={t('help.searchPlaceholder')}
              placeholder={t('help.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="ps-10 bg-white/60 backdrop-blur-xl border-white/40 focus:border-[#7367FF]"
            />
          </div>

          {/* Quick Actions */}
          <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-4 sm:p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-[#434343] mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5 text-[#7367FF]" />
              {t('help.quickStart')}
            </h3>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <QuickTip icon={Upload} label={t('help.quickTips.uploadLabel')} tip={t('help.quickTips.uploadTip')} />
              <QuickTip icon={Volume2} label={t('help.quickTips.listenLabel')} tip={t('help.quickTips.listenTip')} />
              <QuickTip icon={Brain} label={t('help.quickTips.explainLabel')} tip={t('help.quickTips.explainTip')} />
              <QuickTip icon={FileText} label={t('help.quickTips.notesLabel')} tip={t('help.quickTips.notesTip')} />
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-4 sm:p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-[#434343] mb-4 flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-[#7367FF]" />
              {t('help.faqTitle')}
            </h3>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-[#7367FF] text-white'
                      : 'bg-white/60 text-[#434343] hover:bg-[#7367FF]/10'
                  }`}
                >
                  {category === 'All' ? t('help.categories.all') : t(`help.categories.${category}`)}
                </button>
              ))}
            </div>
            
            <div className="space-y-2">
              {filteredFAQs.length === 0 ? (
                <p className="text-center text-[#434343]/60 py-4">
                  {t('help.noResults')}
                </p>
              ) : (
                filteredFAQs.map((faq) => (
                  <div
                    key={faq.id}
                    className="border border-white/40 rounded-xl overflow-hidden"
                  >
                    <button
                      aria-expanded={expandedFAQ === faq.id}
                      aria-controls={`help-faq-${faq.id}`}
                      onClick={() => toggleFAQ(faq.id)}
                      className="w-full flex items-center justify-between p-4 text-start hover:bg-white/40 transition-colors"
                    >
                      <span className="font-medium text-[#434343] text-sm sm:text-base pe-4">{faq.question}</span>
                      {expandedFAQ === faq.id ? (
                        <ChevronUp className="h-4 w-4 text-[#7367FF] flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-[#434343]/40 flex-shrink-0" />
                      )}
                    </button>
                    {expandedFAQ === faq.id && (
                      <div id={`help-faq-${faq.id}`} className="px-4 pb-4 text-sm text-[#434343]/80 bg-white/20">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Contact Support */}
          <div className="bg-gradient-to-r from-[#7367FF]/10 to-[#2DD2FE]/10 backdrop-blur-xl border border-[#7367FF]/20 rounded-2xl p-4 sm:p-6 shadow-lg">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] flex items-center justify-center">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-[#434343] mb-2">
                {t('help.stillNeedHelp')}
              </h3>
              <p className="text-sm text-[#434343]/60 mb-4">
                {t('help.supportBody')}
              </p>
              <Button
                onClick={handleContactSupport}
                className="bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90 text-white"
              >
                <Mail className="h-4 w-4 me-2" />
                {t('help.contactSupport')}
              </Button>
            </div>
          </div>

          {/* Version Info */}
          <div className="text-center py-4">
            <p className="text-xs text-[#434343]/40">{t('common.version')}</p>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

// Quick tip component
function QuickTip({ icon: Icon, label, tip }: { icon: React.ElementType; label: string; tip: string }) {
  return (
    <div className="flex flex-col items-center p-3 rounded-xl bg-white/40 hover:bg-[#7367FF]/10 transition-colors">
      <div className="w-10 h-10 rounded-full bg-[#7367FF]/10 flex items-center justify-center mb-2">
        <Icon className="h-5 w-5 text-[#7367FF]" />
      </div>
      <p className="font-medium text-xs text-[#434343]">{label}</p>
      <p className="text-[10px] text-[#434343]/60 text-center">{tip}</p>
    </div>
  );
}
